/**
 * Account lifecycle — shared constants and helpers (web + mobile parity).
 * Backend Cloud Functions are the source of truth for state transitions.
 */

export const ACCOUNT_STATUS = {
  ACTIVE: 'ACTIVE',
  DEACTIVATED: 'DEACTIVATED',
  PENDING_DELETION: 'PENDING_DELETION',
};

export const DELETION_GRACE_PERIOD_DAYS = 30;

export const LIFECYCLE_FIELD_NAMES = [
  'accountStatus',
  'deactivatedAt',
  'deletionRequestedAt',
  'scheduledDeletionAt',
];

export function resolveAccountStatus(user) {
  if (!user) {
    return ACCOUNT_STATUS.ACTIVE;
  }
  return user.accountStatus || ACCOUNT_STATUS.ACTIVE;
}

export function isAccountActive(user) {
  return resolveAccountStatus(user) === ACCOUNT_STATUS.ACTIVE;
}

export function isAccountDeactivated(user) {
  return resolveAccountStatus(user) === ACCOUNT_STATUS.DEACTIVATED;
}

export function isAccountPendingDeletion(user) {
  return resolveAccountStatus(user) === ACCOUNT_STATUS.PENDING_DELETION;
}

export function parseFirestoreTimestamp(value) {
  if (!value) {
    return null;
  }
  if (typeof value.toDate === 'function') {
    return value.toDate();
  }
  if (value instanceof Date) {
    return value;
  }
  if (typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000);
  }
  return null;
}

export function formatLifecycleDate(value, locale = 'sl-SI') {
  const date = parseFirestoreTimestamp(value);
  if (!date) {
    return '';
  }
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

export function applyLifecycleFieldsToUser(user, lifecycleFields = {}) {
  return {
    ...user,
    accountStatus: lifecycleFields.accountStatus ?? user.accountStatus ?? ACCOUNT_STATUS.ACTIVE,
    deactivatedAt: lifecycleFields.deactivatedAt ?? user.deactivatedAt ?? null,
    deletionRequestedAt: lifecycleFields.deletionRequestedAt ?? user.deletionRequestedAt ?? null,
    scheduledDeletionAt: lifecycleFields.scheduledDeletionAt ?? user.scheduledDeletionAt ?? null,
  };
}

export function mapLifecycleError(error) {
  const code = error?.code || '';
  const message = error?.message || '';

  if (code === 'functions/unauthenticated') {
    return 'Za to dejanje morate biti prijavljeni.';
  }
  if (message.includes('Invalid account transition')) {
    return 'Tega dejanja trenutno ni mogoče izvesti.';
  }
  if (code === 'functions/unavailable' || code === 'functions/not-found') {
    return 'Storitev trenutno ni na voljo. Poskusite znova pozneje.';
  }

  return 'Prišlo je do napake. Poskusite znova.';
}
